import {
  AcademicCapIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  CogIcon,
  EyeIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

// Import screenshot images
import providerPortalImg from '../../assets/screenshots/provider portal.png';
import reviewQueueImg from '../../assets/screenshots/review queue.png';
import schoolDashboardImg from '../../assets/screenshots/school dashboard.png';
import schoolManagementImg from '../../assets/screenshots/school management.png';
import serviceRequestsImg from '../../assets/screenshots/service requests.png';
import workflowRulesImg from '../../assets/screenshots/workflow rules.png';

const WorkflowStorySection: React.FC = () => {
  const workflowSteps = [
    {
      step: 1,
      title: 'Schools Start Here',
      subtitle: 'Complete Overview & Quick Actions',
      description:
        'Schools begin with a comprehensive dashboard showing all service cases, status overviews, and quick-start guides. Everything you need to manage student services in one place.',
      image: schoolDashboardImg,
      icon: AcademicCapIcon,
      features: [
        'Service case overview with real-time status',
        'Quick actions for new requests',
        'Associated schools management',
        'Student management tools',
      ],
    },
    {
      step: 2,
      title: "Tracking Every Student's Needs",
      subtitle: 'Detailed Request Management',
      description:
        'View, filter, and manage all service cases with detailed tracking. See exactly where each student stands in the process with comprehensive status updates and outreach tracking.',
      image: serviceRequestsImg,
      icon: ClipboardDocumentListIcon,
      features: [
        'Comprehensive service case tracking',
        'Provider assignment status',
        'Automated outreach monitoring',
        'Detailed student and service information',
      ],
    },
    {
      step: 3,
      title: 'AI-Powered Automation',
      subtitle: 'Intelligent Workflow Configuration',
      description:
        'Configure intelligent workflows that automatically match providers to service cases based on location, specialties, and availability. AI handles the complex matching while you maintain control.',
      image: workflowRulesImg,
      icon: CogIcon,
      features: [
        'AI-driven provider matching',
        'Automated workflow triggers',
        'Customizable rule configuration',
        'Standard and AI workflow options',
      ],
    },
    {
      step: 4,
      title: 'Human Oversight When Needed',
      subtitle: 'Quality Control & Decision Review',
      description:
        'The system flags items requiring human review, ensuring quality while maintaining efficiency. Review provider matches, escalations, and important decisions in a centralized queue.',
      image: reviewQueueImg,
      icon: EyeIcon,
      features: [
        'Automated review queue generation',
        'Priority-based item sorting',
        'Workflow and priority filtering',
        'Streamlined decision-making interface',
      ],
    },
    {
      step: 5,
      title: 'Providers Respond Efficiently',
      subtitle: 'Clean, Actionable Provider Interface',
      description:
        "Providers receive clear, detailed service cases with all necessary information. They can quickly accept or decline with full context about the student's needs and service requirements.",
      image: providerPortalImg,
      icon: UserCircleIcon,
      features: [
        'Clear service case details',
        'Student and school information',
        'Secure links - no provider login required',
        'Simple accept/decline workflow',
      ],
    },
    {
      step: 6,
      title: 'Administrative Control',
      subtitle: 'System Administration & School Setup',
      description:
        "Comprehensive school management with detailed information tracking, user assignments, and system configuration. Maintain complete control over your organization's setup.",
      image: schoolManagementImg,
      icon: BuildingOffice2Icon,
      features: [
        'Complete school information management',
        'User role and permission control',
        'District and contact management',
        'Audit trail and compliance tracking',
      ],
    },
  ];

  return (
    <section
      id="workflow"
      className="section-padding bg-gradient-to-b from-secondary-50 to-white"
    >
      <div className="container-padding">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-4">
            From Request to Service: The Complete Journey
          </h2>
          <p className="text-xl text-secondary-600 max-w-4xl mx-auto">
            See how TherapyLink transforms the entire workflow from initial
            service case to provider assignment, replacing manual processes
            with intelligent automation while maintaining quality and
            compliance.
          </p>
        </div>

        {/* Workflow Steps */}
        <div className="space-y-24">
          {workflowSteps.map((step, index) => (
            <div
              key={step.step}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } items-center gap-12 lg:gap-16`}
            >
              {/* Content Side */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full">
                    <step.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-primary-600 bg-primary-100 px-3 py-1 rounded-full">
                      Step {step.step}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-lg font-medium text-primary-600 mb-4">
                    {step.subtitle}
                  </p>
                  <p className="text-secondary-600 text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {step.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />
                      <span className="text-secondary-700 text-sm">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Side */}
              <div className="flex-1 max-w-2xl">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300" />
                  <div className="relative bg-white p-2 rounded-2xl shadow-xl">
                    <img
                      src={step.image}
                      alt={`${step.title} - TherapyLink workflow step showing ${step.description.slice(0, 50)}...`}
                      className="w-full h-auto rounded-lg shadow-lg"
                      loading="lazy"
                      width="800"
                      height="600"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-20">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-secondary-900 mb-4">
              Ready to Transform Your Workflow?
            </h3>
            <p className="text-secondary-600 mb-6">
              See how TherapyLink can streamline your therapy services with
              intelligent automation and comprehensive management tools.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary text-lg px-8 py-4"
            >
              Schedule Your Demo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowStorySection;
